import { useState, useRef, useEffect } from 'react';
import { Box, Typography, CircularProgress } from '@mui/material';

import THEME from '../../../constants/theme';

/**
 * VideoPlayer component that displays a video with optional overlay content
 *
 * @param {Object} props - Component props
 * @param {string} props.videoSrc - URL of the video source
 * @param {boolean} props.autoPlay - Whether to auto-play the video (default: false)
 * @param {boolean} props.muted - Whether to mute the video (default: true)
 * @param {boolean} props.controls - Whether to show video controls (default: true)
 * @param {boolean} props.loop - Whether to loop the video (default: false)
 * @param {boolean} props.fullWidth - Whether to take up the full width (default: false)
 * @param {number} props.overlayOpacity - Opacity for the overlay (default: 0.1)
 * @param {string|Object} props.height - Custom height for the video container (supports responsive object)
 * @param {boolean} props.useCustomContainer - Use a custom Box container
 * @param {Object} props.overlay - Optional overlay configuration
 * @param {Object} props.containerSx - Additional styles for the container
 * @returns {React.Component} VideoPlayer component
 */
const VideoPlayer = ({
  videoSrc,
  autoPlay = false,
  muted = true,
  controls = true,
  loop = false,
  fullWidth = false,
  overlayOpacity = 0.1,
  height = null, // Accepts string or object for responsive
  useCustomContainer = false,
  overlay = null,
  containerSx = {}
}) => {
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);
  const videoRef = useRef(null);

  useEffect(() => {
    if (videoRef.current && autoPlay) {
      videoRef.current.load();
      const playPromise = videoRef.current.play();
      if (playPromise !== undefined) {
        playPromise.catch(() => {
          // Video autoplay failed - this is expected behavior in many browsers
        });
      }
    }
  }, [autoPlay]);

  const handleVideoLoad = () => setIsLoading(false);
  const handleVideoError = () => {
    setIsLoading(false);
    setHasError(true);
  };

  if (useCustomContainer) {
    return (
      <Box
        sx={{
          position: 'relative',
          height: height || '500px', // Accepts string or object for breakpoints
          width: '100%',
          maxWidth: '1200px',
          margin: '0 auto',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          overflow: 'hidden',
          borderRadius: '4px',
          ...containerSx
        }}
      >
        {/* Video */}
        <video
          ref={videoRef}
          autoPlay={autoPlay}
          loop={loop}
          muted={muted}
          controls={controls}
          playsInline
          style={{
            position: 'absolute',
            width: fullWidth ? '100%' : '80%',
            height: '100%',
            left: '50%',
            top: '50%',
            objectFit: 'cover',
            transform: 'translate(-50%, -50%)',
            zIndex: 0
          }}
          onLoadedData={handleVideoLoad}
          onError={handleVideoError}
        >
          <source src={videoSrc} type='video/mp4' />
          Your browser does not support the video tag.
        </video>

        {/* Dark Overlay */}
        <Box
          sx={{
            position: 'absolute',
            height: '100%',
            width: fullWidth ? '100%' : '80%',
            top: 0,
            left: fullWidth ? 0 : '10%',
            right: 0,
            bottom: 0,
            backgroundColor: `rgba(0, 0, 0, ${overlayOpacity})`,
            zIndex: 1
          }}
        />

        {/* Overlay content */}
        {overlay && (
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              zIndex: 2,
              padding: overlay.customContent
                ? 0
                : { xs: '0.5rem', sm: '1rem', md: '2rem' },
              width: '100%',
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: overlay.position?.vertical || 'center',
              pointerEvents: 'auto'
            }}
          >
            {overlay.customContent ? (
              overlay.customContent
            ) : (
              <>
                {overlay.title && (
                  <Typography
                    variant={overlay.titleVariant || 'h5'}
                    component={overlay.titleTag || overlay.titleVariant || 'h4'}
                    sx={{
                      fontSize: overlay.titleSize || {
                        xs: '1.5rem',
                        sm: '1.8rem',
                        md: '2rem'
                      },
                      fontWeight: 600,
                      color: overlay.titleColor || THEME.COLORS.PRIMARY_BLUE,
                      marginBottom: '1rem',
                      textShadow: '0 2px 10px rgba(0,0,0,0.3)',
                      fontFamily: THEME.TYPOGRAPHY.FONT_FAMILY.PRIMARY,
                      textAlign: overlay.position?.textAlign || 'center',
                      ...overlay.titleSx
                    }}
                  >
                    {overlay.title}
                  </Typography>
                )}
                {overlay.text && (
                  <Typography
                    variant='body1'
                    sx={{
                      fontSize: overlay.textSize || {
                        xs: '1rem',
                        sm: '1.1rem',
                        md: '1.2rem'
                      },
                      fontWeight: 400,
                      color: overlay.textColor || THEME.COLORS.NEUTRAL_WHITE,
                      textShadow: '0 2px 10px rgba(0,0,0,0.3)',
                      fontFamily: THEME.TYPOGRAPHY.FONT_FAMILY.SECONDARY,
                      maxWidth: overlay.maxWidth || '80%',
                      margin: '0 auto',
                      lineHeight: 1.5,
                      textAlign: overlay.position?.textAlign || 'center',
                      ...overlay.textSx
                    }}
                  >
                    {overlay.text}
                  </Typography>
                )}
              </>
            )}
          </Box>
        )}

        {/* Loading state */}
        {isLoading && (
          <Box
            sx={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              zIndex: 2
            }}
          >
            <CircularProgress sx={{ color: THEME.COLORS.PRIMARY_BLUE }} />
          </Box>
        )}
      </Box>
    );
  }

  // Standard container option (not often used in your project)
  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        width: '100%',
        maxWidth: '1200px',
        margin: '0 auto',
        padding: { xs: 2, md: 4 },
        ...containerSx
      }}
    >
      {/* Video Container */}
      <Box
        sx={{
          width: '100%',
          overflow: 'hidden',
          position: 'relative',
          height: height || '400px',
          backgroundColor: THEME.COLORS.NEUTRAL_BLACK,
          borderRadius: '4px'
        }}
      >
        {/* Video */}
        <video
          ref={videoRef}
          src={videoSrc}
          controls={controls}
          autoPlay={autoPlay}
          muted={muted}
          loop={loop}
          playsInline
          style={{ width: '100%', height: '100%', objectFit: 'cover' }}
          onLoadedData={handleVideoLoad}
          onError={handleVideoError}
        />

        {/* Loading and Error states */}
        {isLoading && (
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: 'rgba(0, 0, 0, 0.1)',
              zIndex: 2
            }}
          >
            <CircularProgress sx={{ color: THEME.COLORS.PRIMARY_BLUE }} />
          </Box>
        )}
        {hasError && (
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: '#f8f8f8',
              zIndex: 2
            }}
          >
            <Typography
              variant='body1'
              sx={{
                color: THEME.COLORS.NEUTRAL_RED,
                fontFamily: THEME.TYPOGRAPHY.FONT_FAMILY.SECONDARY
              }}
            >
              There was an error loading the video.
            </Typography>
          </Box>
        )}

        {/* Overlay content */}
        {overlay && (
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: overlay.position?.vertical || 'center',
              alignItems: overlay.position?.horizontal || 'center',
              padding: overlay.customContent ? 0 : { xs: 1, md: 1 },
              background:
                overlay.background || `rgba(0, 0, 0, ${overlayOpacity})`,
              zIndex: 1,
              pointerEvents: 'auto'
            }}
          >
            {overlay.customContent ? (
              overlay.customContent
            ) : (
              <>
                {overlay.title && (
                  <Typography
                    variant='h3'
                    sx={{
                      color: overlay.titleColor || THEME.COLORS.NEUTRAL_WHITE,
                      fontFamily: THEME.TYPOGRAPHY.FONT_FAMILY.PRIMARY,
                      fontWeight: 600,
                      marginBottom: 2,
                      textShadow: '1px 1px 3px rgba(0,0,0,0.7)',
                      fontSize: { xs: '1.8rem', sm: '2.2rem', md: '2.5rem' },
                      textAlign: overlay.position?.textAlign || 'center',
                      ...overlay.titleSx
                    }}
                  >
                    {overlay.title}
                  </Typography>
                )}
                {overlay.text && (
                  <Typography
                    variant='body1'
                    sx={{
                      color: overlay.textColor || THEME.COLORS.NEUTRAL_WHITE,
                      fontFamily: THEME.TYPOGRAPHY.FONT_FAMILY.SECONDARY,
                      fontSize: { xs: '1rem', sm: '1.1rem', md: '1.3rem' },
                      maxWidth: overlay.maxWidth || '80%',
                      textShadow: '1px 1px 2px rgba(0,0,0,0.7)',
                      lineHeight: 1.6,
                      textAlign: overlay.position?.textAlign || 'center',
                      ...overlay.textSx
                    }}
                  >
                    {overlay.text}
                  </Typography>
                )}
              </>
            )}
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default VideoPlayer;
